import { useState } from 'react';
import styled from 'styled-components';
import validator from 'validator';
import _ from 'lodash';
import { Checkbox, Row, Col, notification, Spin, Input, Form } from 'antd';
import { ErrorField } from 'v3/components/pitstop';
import { AppStore, CurrentUserStore, ShopStore, UserStore } from 'stores';
import Logger from 'stores/Classes/Logger';

const slogans = [
  'Register now and explore Pitstop with demo data.',
  'Sign up to catch issues early and save time.',
  'Register today and discover root causes with AI.',
  'Create your account to digitize and automate maintenance.',
  'Sign up now to streamline your fleet workflows.',
  'Register today and scan work orders instantly with AI.',
  'Sign up to optimize fault priorities in real-time.',
  'Create your account to save money and keep fleets moving.',
  'Register now to experience smarter fleet management.',
  'Sign up today—your Pitstop AI assistant is ready.',
  'Fleet maintenance simplified by Pitstop AI.',
];

const getRandomMessage = () =>
  slogans[Math.floor(Math.random() * slogans.length)];

const Container = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 600;
    color: #000000;
    text-align: center;
  }

  p {
    font-weight: 400;
    color: #8c8c8c;
    opacity: 0.9;
    text-align: center;
  }

  label {
    font-weight: 600;
  }
`;

const DashLine = styled.div`
  opacity: 0.2;
  border-top: 2px dashed #000000;
  width: 100%;
`;

const RegisterButton = styled.button`
  cursor: pointer;
  border: none;
  background: #02103d;
  color: #ffffff;
  border-radius: 5px;
  height: 3rem;
  min-width: 8rem;
  font-weight: bold;
  margin-top: 1rem;
`;

const RegisterButtonContainer = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 1.6vw;
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
`;

const RegisterForm = () => {
  const [form] = Form.useForm();
  const [formState, setFormState] = useState({
    isAcceptedAgreement: false,
    isSubmitted: false,
    slogan: getRandomMessage(),
  });

  const getError = (type, value) => {
    if (_.isEmpty(value) || _.isNil(value)) {
      return <ErrorField>{type} is required!</ErrorField>;
    }

    if (type === 'email' && !validator.isEmail(value)) {
      return <ErrorField>Email is invalid</ErrorField>;
    }

    if (type === 'password' && value.match(/(\s)/g)) {
      return <ErrorField>Password cannot have blank spaces</ErrorField>;
    }

    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormState((prev) => ({ ...prev, isSubmitted: true }));

    try {
      const values = await form.validateFields();
      const { email, password, fleetName } = values;

      if (
        getError('email', email) ||
        getError('password', password) ||
        getError('fleetName', fleetName) ||
        !formState.isAcceptedAgreement
      ) {
        return;
      }

      notification.open({
        key: 'register',
        description: (
          <>
            <Spin size="large" style={{ marginRight: '0.6rem' }} />
            Your account is being created...
          </>
        ),
        duration: 0,
      });

      await UserStore.createUser({
        username: email,
        email: email,
        password: password,
        role: 'dealership',
      });
      notification.destroy('register');
      AppStore.addSuccess(
        'Successfully added user, logging into dashboard now...'
      );

      let user = await CurrentUserStore.login(email, password);

      await ShopStore.addShop({
        userId: user.id,
        name: fleetName,
        email: email,
        address: '',
      });
      await CurrentUserStore.getCurrentUser();
      await CurrentUserStore.createHubspotContact({
        email: email,
        form_name: 'dashboard-registration',
      });
      await CurrentUserStore.user.updateSettings({
        hasAcceptedEULA: true,
      });
    } catch (err) {
      const { email, fleetName } = form.getFieldsValue();
      Logger.error(err.message, { fleetName, email });
      notification.error({
        message: 'Registration Error',
        description: err.message || 'An error occurred during registration',
        duration: 5,
      });
    } finally {
      notification.destroy('register');
    }
  };

  const { isSubmitted, isAcceptedAgreement, slogan } = formState;

  return (
    <Container id="register-form" form={form} labelCol={{ span: 24 }}>
      <h1>Try Pitstop AI for free</h1>

      <p>{slogan}</p>
      <DashLine />

      <Row>
        <StyledFormItem
          label="Enter your work email"
          name="email"
          rules={[
            { required: true, message: 'Email is required!' },
            { type: 'email', message: 'Email is invalid' },
          ]}
        >
          <Input placeholder="Work email (e.g., name@company.com)" />
        </StyledFormItem>

        <StyledFormItem
          label="Set a new password"
          name="password"
          rules={[
            { required: true, message: 'Password is required!' },
            { min: 6, message: 'Password must be at least 6 characters' },
            {
              pattern: /^[^\s]+$/,
              message: 'Password cannot have blank spaces',
            },
          ]}
        >
          <Input.Password placeholder="Password (min. 6 characters)" />
        </StyledFormItem>

        <StyledFormItem
          label="Fleet Name"
          name="fleetName"
          rules={[{ required: true, message: 'Fleet name is required!' }]}
        >
          <Input placeholder="Your awesome fleet name" />
        </StyledFormItem>

        <Col
          span={24}
          style={{
            display: 'flex',
            alignItems: 'middle',
            marginTop: '20px',
          }}
        >
          <Checkbox
            checked={isAcceptedAgreement}
            style={{ marginRight: '0.6vw' }}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                isAcceptedAgreement: e.target.checked,
              }))
            }
          />
          <p style={{ margin: 0 }}>
            I agree with{' '}
            <a
              style={{
                fontWeight: 600,
                color: '#000000',
                fontStyle: 'italic',
              }}
              href="https://pitstopconnect.com/terms-conditions-privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              terms and conditions
            </a>
          </p>
        </Col>
        {isSubmitted && !isAcceptedAgreement && (
          <ErrorField style={{ textAlign: 'left' }}>
            You must accept Terms and Conditions
          </ErrorField>
        )}

        <RegisterButtonContainer>
          <RegisterButton onClick={onSubmit}>Register</RegisterButton>
        </RegisterButtonContainer>
      </Row>
    </Container>
  );
};

export default RegisterForm;
